import MuiBottomNavigation from '@mui/material/BottomNavigation'
import BottomNavigationAction, { bottomNavigationActionClasses } from '@mui/material/BottomNavigationAction'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { ACTIVE_TAB_HEIGHT, CATEGORIES_ROUTE, EVENTS_ROUTE, NEWS_ROUTE, PLACES_ROUTE } from 'shared'
import { RegionModel } from 'shared/api'

import { BOTTOM_NAVIGATION_ELEMENT_ID } from '../constants/layout'
import useDimensions from '../hooks/useDimensions'
import useRegionContentParams from '../hooks/useRegionContentParams'
import useUpdateDimensions from '../hooks/useUpdateDimensions'
import getNavigationItems from '../utils/navigationItems'
import Link from './base/Link'

const Container = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  // Positon bottom navigation above bottom sheet
  zIndex: 10,
  // Remove the space under the bottom navigation for chrome on iOS
  // https://github.com/digitalfabrik/integreat-app/issues/3968
  paddingBottom: 'env(safe-area-inset-bottom)',
  backgroundColor: theme.palette.background.accent,
  backgroundImage: 'none',
}))

const StyledBottomNavigation = styled(MuiBottomNavigation)(({ theme }) => ({
  backgroundColor: theme.palette.background.accent,
})) as typeof MuiBottomNavigation

const StyledBottomNavigationAction = styled(BottomNavigationAction)(({ theme }) => ({
  color: theme.palette.text.secondary,

  [`&.${bottomNavigationActionClasses.selected}`]: {
    color: theme.palette.primary.main,

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '10%',
      right: '10%',
      height: ACTIVE_TAB_HEIGHT,
      borderRadius: `0 0 20px 20px`,
      backgroundColor: 'currentColor',
    },
  },

  [theme.breakpoints.down('sm')]: {
    flex: '1 1 auto',
    minWidth: 'unset',
    overflow: 'hidden',

    [`.${bottomNavigationActionClasses.label}`]: {
      maxWidth: '100%',
    },
  },
})) as typeof BottomNavigationAction

type BottomNavigationProps = {
  regionModel: RegionModel
  languageCode: string
}

const BottomNavigation = ({ regionModel, languageCode }: BottomNavigationProps): ReactElement | null => {
  const { route } = useRegionContentParams()
  const { t } = useTranslation()
  const { xsmall } = useDimensions()
  useUpdateDimensions()

  const navigationItems = getNavigationItems({ regionModel, languageCode })
  const validTabValues: string[] = [CATEGORIES_ROUTE, PLACES_ROUTE, NEWS_ROUTE, EVENTS_ROUTE]
  const value = validTabValues.includes(route) ? route : false

  if (!navigationItems) {
    return null
  }

  return (
    <Container elevation={2}>
      <StyledBottomNavigation id={BOTTOM_NAVIGATION_ELEMENT_ID} showLabels value={value} component='nav'>
        {navigationItems.map(item => {
          const label = t($ => (item.value === CATEGORIES_ROUTE ? $.categories.titleShort : $[item.value].title))
          return (
            <StyledBottomNavigationAction
              key={item.value}
              component={Link}
              to={item.to}
              value={item.value}
              label={
                xsmall ? (
                  <Typography
                    component='div'
                    variant='body3'
                    sx={{
                      fontWeight: value === item.value ? 'bold' : 'normal',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                    {label}
                  </Typography>
                ) : (
                  label
                )
              }
              icon={<item.Icon />}
            />
          )
        })}
      </StyledBottomNavigation>
    </Container>
  )
}

export default BottomNavigation
